import { useContext, useEffect, useState } from "react";
import { DatabaseSearchContext, DateView } from "nystem-components";

const ViewListTibberGraph = () => {
  const { search, loading } = useContext(DatabaseSearchContext);
  const [now, setNow] = useState(Date.now());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const start = Math.floor(now / 900000) * 900000;
  const prices = (search.data || [])
    .filter(({ _crdate, price }) => _crdate >= start && _crdate < start + 86400000 && Number.isFinite(price))
    .sort((a, b) => a._crdate - b._crdate);
  if (!prices.length)
    return (
      <p className="py-6 text-sm text-gray-400">{loading ? "Loading prices…" : "Prices are not available yet."}</p>
    );

  const end = Number(prices[prices.length - 1]._crdate) + 900000;
  const height = ((end - start) / 86400000) * 480;
  const lowest = Math.min(...prices.map(({ price }) => price));
  const highest = Math.max(...prices.map(({ price }) => price));
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, highest - lowest) / 8));
  const step = [1, 2, 2.5, 5, 10].find((value) => value * magnitude >= (highest - lowest) / 8) * magnitude;
  const minimum = Math.floor(lowest / step) * step;
  const maximum = Math.max(minimum + step, Math.ceil(highest / (step / 5)) * (step / 5));
  const ticks = Array.from({ length: Math.floor((maximum - minimum) / step) + 1 }, (_, pos) => minimum + pos * step);
  const cheapest = prices.reduce((lowest, price) => (price.price < lowest.price ? price : lowest));
  const active =
    prices.find(({ _id }) => _id === selected) || prices.find(({ _crdate }) => _crdate === start) || prices[0];

  return (
    <section className="mt-4" aria-label="Tibber electricity prices">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-100">Electricity prices</h3>
        <div className="ml-auto flex flex-wrap items-baseline justify-end gap-2">
          <span className="flex items-center gap-1 text-xs text-gray-400">
            {active._crdate === start && <span>Now ·</span>}
            <DateView model={{ dateFormat: "time", renderAs: "span" }} value={active._crdate} />
            <span>–</span>
            <DateView model={{ dateFormat: "time", renderAs: "span" }} value={Number(active._crdate) + 900000} />
          </span>
          <strong className="w-20 text-right text-xl font-semibold leading-tight text-sky-300 tabular-nums">
            {active.price.toFixed(1)}
          </strong>
        </div>
      </div>
      <div className="mt-3 text-right text-xs text-emerald-300">
        <span>Lowest {cheapest.price.toFixed(1)} · </span>
        {cheapest._crdate >= new Date(now).setHours(24, 0, 0, 0) && <span>Tomorrow </span>}
        <DateView model={{ dateFormat: "time", renderAs: "span" }} value={cheapest._crdate} />
      </div>
      <div className="relative mt-2 pr-3 pl-12">
        <div className="relative h-6 text-xs leading-none text-gray-300 tabular-nums">
          {ticks.map((price) => (
            <span
              key={price}
              className="absolute top-0 -translate-x-1/2"
              style={{ left: `${((price - minimum) / (maximum - minimum)) * 100}%` }}
            >
              {Number(price.toPrecision(12))}
            </span>
          ))}
        </div>
        <div
          className="pointer-events-none absolute top-6 left-0 text-xs text-gray-300 tabular-nums"
          style={{ height }}
        >
          {Array.from({ length: 25 }, (_, hour) => Math.ceil(start / 3600000) * 3600000 + hour * 3600000)
            .filter((time) => time < end)
            .map((time) => (
              <span
                key={time}
                className="absolute left-0 -translate-y-1/2 whitespace-nowrap"
                style={{ top: `calc(${((time - start) / (end - start)) * 100}% + 2.5px)` }}
              >
                <DateView model={{ dateFormat: "time", renderAs: "span" }} value={time} />
              </span>
            ))}
        </div>
        <svg
          className="w-full overflow-visible"
          style={{ height }}
          viewBox={`0 0 960 ${height}`}
          preserveAspectRatio="none"
          aria-label="Available upcoming prices, one bar per 15 minutes"
          onMouseLeave={() => {
            setSelected(null);
            setNow(Date.now());
          }}
        >
          {ticks.map((price) => (
            <line
              key={price}
              x1={((price - minimum) / (maximum - minimum)) * 960}
              x2={((price - minimum) / (maximum - minimum)) * 960}
              y1="0"
              y2={height}
              stroke="currentColor"
              className="text-gray-500/60"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {prices.map(({ _id, _crdate, price }) => {
            const y = ((_crdate - start) / 86400000) * 480;
            return (
              <g
                key={_id}
                tabIndex={0}
                role="button"
                aria-label={`Price ${price.toFixed(1)} öre per kWh`}
                aria-pressed={active._id === _id}
                className={`cursor-pointer outline-none transition-colors ${active._id === _id ? "fill-sky-300/70" : price === cheapest.price ? "fill-emerald-400/50" : price > (maximum + minimum) / 2 ? "fill-amber-400/45" : "fill-sky-500/45"} focus:stroke-white focus:stroke-2`}
                onMouseEnter={() => setSelected(_id)}
                onFocus={() => setSelected(_id)}
                onClick={(event) => {
                  event.preventDefault();
                  setSelected(_id);
                }}
                onKeyDown={(event) => {
                  if (["Enter", " "].includes(event.key)) {
                    event.preventDefault();
                    setSelected(_id);
                  }
                }}
              >
                <rect
                  x="0"
                  y={y + 0.5}
                  width={Math.max(2, ((price - minimum) / (maximum - minimum)) * 960)}
                  height="4"
                  rx="1"
                />
                <rect x="0" y={y} width="960" height="5" fill="transparent" stroke="none" />
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
};

export default ViewListTibberGraph;
